import { z } from 'zod';
import { Email } from './primitives.js';
import { FALLBACK_COPY, OFFLINE_COPY, TOO_MANY_REQUESTS_COPY } from './problems.js';
/**
 * Forms for the Better Auth endpoints under `/auth` (docs/architecture/auth.md).
 * Those endpoints are called through the Better Auth client, not our OpenAPI
 * client, but the shapes the screens validate still live here.
 */
/** 10 to 128 characters and no composition rules, as the API enforces. */
export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MAX_LENGTH = 128;
export const NewPassword = z
    .string()
    .min(PASSWORD_MIN_LENGTH, `Use at least ${PASSWORD_MIN_LENGTH} characters.`)
    .max(PASSWORD_MAX_LENGTH, `Keep it under ${PASSWORD_MAX_LENGTH} characters.`);
/** Sign in checks only that something was typed; the API says whether it is right. */
/**
 * The local development admin. The seed creates it with the password `admin`
 * on a local development database only, and the back office's dev build
 * accepts `admin` as shorthand for this email. Production never has it.
 */
export const DEV_ADMIN_EMAIL = 'admin@myhomeplus.local';
export const DEV_ADMIN_USERNAME = 'admin';
export const SignInInput = z.object({
    email: Email,
    password: z.string().min(1, 'Enter your password.'),
});
export const RequestPasswordResetInput = z.object({ email: Email });
/** The body the reset screen sends, less the token from the link. */
export const ResetPasswordInput = z.object({ newPassword: NewPassword });
/**
 * The reset form, and the admin invitation form, which is a reset by another
 * name. The confirmation is client-only, so it extends the request shape.
 */
export const ResetPasswordFormInput = ResetPasswordInput.extend({ confirmPassword: z.string() }).refine((value) => value.newPassword === value.confirmPassword, { path: ['confirmPassword'], message: 'The passwords do not match.' });
// --- Error copy ------------------------------------------------------------
/**
 * `/auth/*` answers with Better Auth's `{ code, message }` instead of
 * problem+json (ADR 0003, trade-off 3). The screens show this copy, keyed by
 * `code`, and never the raw `message`.
 */
const AUTH_ERROR_COPY = {
    // Unknown email and wrong password get the same words on purpose.
    INVALID_EMAIL_OR_PASSWORD: 'That email and password do not match. Check them and try again.',
    BANNED_USER: 'This account has been suspended. Contact MyHome+ if you think this is a mistake.',
    INVALID_TOKEN: 'This link is not valid any more. Ask for a new one.',
    TOKEN_EXPIRED: 'This link has expired. Links last 1 hour, so ask for a new one.',
    PASSWORD_TOO_SHORT: `Use at least ${PASSWORD_MIN_LENGTH} characters.`,
    PASSWORD_TOO_LONG: `Keep it under ${PASSWORD_MAX_LENGTH} characters.`,
    EMAIL_ALREADY_VERIFIED: 'Your email address is already confirmed.',
    EMAIL_MISMATCH: 'That is not the email address on this account.',
    USER_NOT_FOUND: 'This link is not valid any more. Ask for a new one.',
    INVALID_USER: 'This link belongs to a different account. Sign out and open it again.',
};
/** The words for a failed `/auth/*` call. `status` 0 means the request never arrived. */
export function authErrorMessage(error) {
    if (error.status === 429)
        return TOO_MANY_REQUESTS_COPY;
    if (error.code !== undefined) {
        const copy = AUTH_ERROR_COPY[error.code];
        if (copy !== undefined)
            return copy;
    }
    if (error.status === 0 || error.status === undefined)
        return OFFLINE_COPY;
    return FALLBACK_COPY;
}
//# sourceMappingURL=auth.js.map